export const findAccountGuideMarkdown = `
# Find My Account

Use **Find My Account** when you were invited to VetNebula but never finished setting up your password, or when you are not sure which email address is on your account.

---

## 1. Open Find My Account

1. Go to the VetNebula sign-in page.
2. Click **Find My Account** (green button below **Send A Login Code**).

![Sign-in page with Find My Account button](/images/guides/find-account/01-sign-in.png)

---

## 2. Choose how to search

On the Find My Account page, pick one of two options:

- **By Email** — use this when you know the email address you were invited with.
- **By Name** — use this when you do not remember the email, but you know your first name, last name, and role (Director, Coach, or Learner).

![Find My Account — search by email](/images/guides/find-account/02-find-account-email.png)

### By Email

1. Leave **By Email** selected.
2. Enter your email address.
3. Click **Find My Account**.

**Already have an account with the same email?**  
If you use VetNebula in another role (for example, as a student or director) and were also invited as a coach, use the **coach alias** you were given — such as \`john.doe+coach@bluepearlvet.com\` — instead of your regular work email.

### By Name

1. Click **By Name**.
2. Enter your **First Name** and **Last Name** exactly as they appear in your invitation.
3. Select your role: **Director**, **Coach**, or **Learner**.
4. Click **Find My Account**.

![Find My Account — search by name and role](/images/guides/find-account/03-find-account-name.png)

---

## 3. Check your email

If we find your account, you will see a confirmation on screen and receive an email within a few minutes.

The email includes:

- Your VetNebula email address
- A **temporary password**
- A **Sign In to VetNebula** link

Check your **inbox** and **spam/junk** folder if you do not see it right away.

---

## 4. Sign in and set your password

1. Open the email and click **Sign In to VetNebula**, or return to the sign-in page and enter your email and the temporary password.
2. After you sign in, VetNebula will ask you to **create a new password**. Choose a password you will remember — you will use it for future sign-ins.
3. You are then taken into the app.

---

## Troubleshooting

- **No account found** — Double-check spelling. For email search, try your \`+coach\` alias if you have another VetNebula role. For name search, confirm the correct role.
- **Multiple accounts match** — Use **By Email** with the specific address from your invitation, or contact your program administrator.
- **No email after several minutes** — Check spam. Try again once. If it still does not arrive, contact support.
- **Temporary password does not work** — Copy the password carefully (no extra spaces). Request a new one with Find My Account if needed.

---

## Quick reference

1. Sign in page → **Find My Account**
2. Search by **email** or **name + role**
3. Open recovery email → sign in with temporary password
4. Set your permanent password on the next screen
`;
